import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import {captureException} from '@sentry/react';
import {PlaidLinkError, PlaidLinkOptions, usePlaidLink} from 'react-plaid-link';
import {useNavigate} from 'react-router';

import {PLAID_UI_EVENTS} from '@/app/auth/events/PlaidUIEvents';
import {useKycRepository} from '@/dataSource/src/api/kyc/useRepository';
import {WEB_VIEW_MESSAGES} from '@/services/webview/constants';
import {WebViewService} from '@/services/webview/WebViewService';
import logError, {logBreadcrumb} from '@/utils/errors/logError';

import {useTrackers} from './trackers';

type ViewModelContextType = {
  openPlaid: () => void;
  isReady: boolean;
  plaidInitError: string | null;
  retryInit: () => void;
};

const ViewModelContext = createContext<ViewModelContextType | undefined>(
  undefined,
);

const idvSubmittedRoute = '/onboarding/kyc/in-review/idv-submitted';

export function ViewModelProvider({children}: {children: ReactNode}) {
  //#region [---- [PROPERTIES] ----]
  const navigate = useNavigate();
  const {handleGetTokenIdvPlaid, plaidIdvToken} = useKycRepository();
  const {
    trackViewKycInReviewIdvPlaidShowWebview,
    trackViewKycInReviewIdvPage,
    trackViewKycInReviewIdvPlaidError,
    trackSelectKycInReviewIdvPlaidExit,
    trackViewKycInReviewIdvPlaidSuccess,
    trackSelectKycInReviewIdvGoToVerification,
    trackPlaidEvent,
  } = useTrackers();
  //#endregion

  //#region [---- [STATE] ----]
  const [plaidInitError, setPlaidInitError] = useState<string | null>(null);
  const pendingOpenRef = useRef<boolean>(false);
  //#endregion

  //#region [---- [PLAID CALLBACKS] ----]
  const onPlaidSuccess = useCallback(() => {
    trackViewKycInReviewIdvPlaidSuccess();
    navigate(idvSubmittedRoute);
  }, [trackViewKycInReviewIdvPlaidSuccess, navigate]);

  const onPlaidExit = useCallback((error?: string) => {
    if (error) {
      logError(error, {
        customTitle: 'KYC IDV - Error on exit',
        fileName: 'Idv ViewModelContext',
        functionName: 'onPlaidExit',
      });

      captureException(new Error(error), {
        tags: {
          featureName: 'KYC IDV',
          screenName: 'Idv',
          service: 'Plaid',
        },
        extra: {
          fileName: 'Idv ViewModelContext',
          functionName: 'onPlaidExit',
          error: error,
        },
      });
    } else {
      logBreadcrumb({
        category: 'KycInReview.Idv.ViewModelContext.onExit',
        message: 'Plaid exit without error',
      });
    }
  }, []);

  const handleOnPlaidExit = useCallback(
    (error?: PlaidLinkError, metadata?: any) => {
      trackPlaidEvent(PLAID_UI_EVENTS.PLAID_ON_EXIT, {
        error,
        metadata,
      });
      if (error) {
        trackViewKycInReviewIdvPlaidError(
          error.error_code,
          error.error_message,
        );
        logError(error, {
          customTitle: 'KYC IDV - Error on Plaid exit',
          fileName: 'Idv ViewModelContext',
          functionName: 'config.onExit',
          data: {metadata},
        });
      } else {
        logBreadcrumb({
          category: 'KycInReview.Idv.ViewModelContext.onExit',
          message: 'Plaid Link exited without error',
          data: {metadata},
        });
        trackSelectKycInReviewIdvPlaidExit();
      }
      onPlaidExit(error?.display_message);
    },
    [
      trackPlaidEvent,
      onPlaidExit,
      trackViewKycInReviewIdvPlaidError,
      trackSelectKycInReviewIdvPlaidExit,
    ],
  );
  //#endregion

  //#region [---- [PLAID CONFIG] ----]
  const config: PlaidLinkOptions = {
    token: plaidIdvToken || null,
    onSuccess: () => {
      onPlaidSuccess();
    },
    onEvent: (eventName, metadata) => {
      trackPlaidEvent(PLAID_UI_EVENTS.PLAID_ON_EVENT, {
        eventName,
        metadata,
      });
      logBreadcrumb({
        category: 'KycInReview.Idv.ViewModelContext.onEvent',
        message: `Plaid event: ${eventName}`,
        type: 'info',
        data: {metadata},
      });
    },
    onLoad: () => {
      trackPlaidEvent(PLAID_UI_EVENTS.PLAID_ON_LOAD);
      logBreadcrumb({
        category: 'KycInReview.Idv.ViewModelContext.onLoad',
        message: 'Plaid Link loaded',
        type: 'info',
      });
    },
    onExit: (error?: PlaidLinkError, metadata?: any) => {
      handleOnPlaidExit(error, metadata);
    },
  };
  //#endregion

  //#region [---- [PLAID LINK] ----]
  const {open, ready, error: plaidError} = usePlaidLink(config);
  //#endregion

  //#region [---- [ACTIONS] ----]
  const triggerOpen = useCallback(() => {
    if (
      WebViewService.isWebViewActive() &&
      typeof window.ReactNativeWebView !== 'undefined' &&
      window.ReactNativeWebView
    ) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          status: WEB_VIEW_MESSAGES.PLAID_REDIRECTION,
          token: plaidIdvToken,
        }),
      );
      return;
    }
    open();
    document.body.style.overflow = '';
  }, [open, plaidIdvToken]);

  const openPlaid = useCallback(() => {
    trackSelectKycInReviewIdvGoToVerification();

    if (ready) {
      trackViewKycInReviewIdvPlaidShowWebview();
      triggerOpen();
    } else {
      // Queue the open — useEffect above fires it once ready === true
      pendingOpenRef.current = true;
      logBreadcrumb({
        category: 'KycInReview.Idv.ViewModelContext.openPlaid',
        level: 'warning',
        message: 'Plaid Link is not ready yet, queuing open',
      });
    }
  }, [
    ready,
    triggerOpen,
    trackViewKycInReviewIdvPlaidShowWebview,
    trackSelectKycInReviewIdvGoToVerification,
  ]);

  const retryInit = useCallback(() => {
    logBreadcrumb({
      category: 'KycInReview.Idv.ViewModelContext.retryInit',
      level: 'warning',
      message: 'Plaid script failed to load — retrying initialization',
    });
    setPlaidInitError(null);
    handleGetTokenIdvPlaid();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //#endregion

  //#region [---- [EFFECTS] ----]
  useEffect(() => {
    trackViewKycInReviewIdvPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleGetTokenIdvPlaid();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle Plaid SDK initialization errors
  useEffect(() => {
    if (plaidError) {
      const errorMessage =
        'We had trouble connecting to our verification partner. Please try again.';

      setPlaidInitError(errorMessage);

      trackPlaidEvent(PLAID_UI_EVENTS.VIEW_KYC_MODAL_VERIFY_WITH_PLAID_ERROR, {
        error: plaidError,
      });

      captureException(
        new Error(plaidError.message ?? 'Plaid IDV init error'),
        {
          tags: {
            featureName: 'KYC IDV',
            screenName: 'Idv',
            service: 'Plaid',
          },
          extra: {
            fileName: 'Idv ViewModelContext',
            functionName: 'usePlaidLink',
            plaidError: JSON.stringify(plaidError),
          },
        },
      );

      logBreadcrumb({
        category: 'KycInReview.Idv.ViewModelContext.plaidError',
        level: 'error',
        message: 'Plaid Link initialization error',
        data: {plaidError},
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plaidError]);

  useEffect(() => {
    if (plaidIdvToken) {
      setPlaidInitError(null);
    }
  }, [plaidIdvToken]);

  // Fire any queued open once Plaid becomes ready
  useEffect(() => {
    if (ready && pendingOpenRef.current) {
      pendingOpenRef.current = false;
      triggerOpen();
    }
  }, [ready, triggerOpen]);
  //#endregion

  return (
    <ViewModelContext.Provider
      value={{
        openPlaid,
        isReady: ready,
        plaidInitError,
        retryInit,
      }}>
      {children}
    </ViewModelContext.Provider>
  );
}

export function useViewModelProvider() {
  const context = useContext(ViewModelContext);
  if (context === undefined) {
    throw new Error('IdvPage ViewModelContext is undefined');
  }
  return context;
}
